import { useState, useEffect, useRef } from 'react';

interface UseFileDragDropResult {
  isDragOver: boolean;
}

/**
 * Window-level file drag tracking. A counter survives nested drag targets, so
 * the overlay stays up while the cursor is anywhere on the page. Disabled, it
 * registers nothing and reports no drag.
 */
export function useFileDragDrop(
  onDrop: (files: FileList | null) => void,
  enabled = true,
): UseFileDragDropResult {
  const [isDragOver, setIsDragOver] = useState(false);

  // Read at drop time, so the listeners never need re-registering.
  const onDropRef = useRef(onDrop);
  useEffect(() => {
    onDropRef.current = onDrop;
  }, [onDrop]);

  useEffect(() => {
    if (!enabled) {
      setIsDragOver(false);
      return undefined;
    }
    let dragCounter = 0;

    const onDragEnter = (e: DragEvent) => {
      if (e.dataTransfer?.types.includes('Files')) {
        dragCounter++;
        setIsDragOver(true);
      }
    };

    const onDragLeave = (e: DragEvent) => {
      if (e.dataTransfer?.types.includes('Files')) {
        dragCounter--;
        if (dragCounter <= 0) {
          dragCounter = 0;
          setIsDragOver(false);
        }
      }
    };

    // Only file drags are intercepted; text and link drops fall through.
    const onDragOver = (e: DragEvent) => {
      if (e.dataTransfer?.types.includes('Files')) {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
      }
    };

    const handleDrop = (e: DragEvent) => {
      if (!e.dataTransfer?.files?.length) return;
      e.preventDefault();
      dragCounter = 0;
      setIsDragOver(false);
      onDropRef.current(e.dataTransfer.files);
    };

    globalThis.addEventListener('dragenter', onDragEnter);
    globalThis.addEventListener('dragleave', onDragLeave);
    globalThis.addEventListener('dragover', onDragOver);
    globalThis.addEventListener('drop', handleDrop);

    return () => {
      globalThis.removeEventListener('dragenter', onDragEnter);
      globalThis.removeEventListener('dragleave', onDragLeave);
      globalThis.removeEventListener('dragover', onDragOver);
      globalThis.removeEventListener('drop', handleDrop);
    };
  }, [enabled]);

  return { isDragOver };
}
